import React, { useState, useEffect, useCallback } from 'react';
import { X, MessageCircle } from 'lucide-react';
import {
  doc, onSnapshot, setDoc, collection, query, orderBy,
  writeBatch, deleteDoc, updateDoc, getDocs, arrayUnion, arrayRemove, getDoc
} from 'firebase/firestore';
import { User } from 'firebase/auth';
import { db, auth } from './services/firebaseConfig';
import { authService } from './services/authService';
import { trackSessionStart, trackSessionEnd, trackLogin, trackPageView } from './services/analyticsService';
import { initFCM, setupFCMListeners, registerNotificationClickHandler } from './services/fcmService';
import { v4 as uuidv4 } from 'uuid';
import { FamilyMember, RelationType, Post } from './types';
import { cleanOrphanedChildIds, fixMissingParentLink, findMissingParentLinks, generateCleanupReport } from './services/dataCleanupService';

// --- COMPONENTS ---
import Layout from './components/Layout';
import { ThemeProvider } from './contexts/ThemeContext';
import FamilyTree from './components/FamilyTree';
import SocialFeed from './components/SocialFeed';
import NetworkView from './components/NetworkView';
import FamilyMap from './components/FamilyMap';
import InviteModal from './components/InviteModal'; // <--- NEW IMPORT
import Directory from './components/Directory';
import FamilyTimeline from './components/FamilyTimeline';
import FamilyStats from './components/FamilyStats';
import Profile from './components/Profile';
import AdminDashboard from './components/AdminDashboard';
import MemberFormModal from './components/MemberFormModal';
import NotificationBell from './components/NotificationBell';
import NotificationPreferences from './components/NotificationPreferences';
import NotificationPermissionRequest from './components/NotificationPermissionRequest';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import SearchModal from './components/SearchModal';
import VideoTutorials from './components/VideoTutorials';
import { AppSkeleton } from './components/SkeletonLoader';
import LoginScreen from './components/LoginScreen'; 

// Super Admin Phone Numbers (Normalized: 10 digits)
const SUPER_ADMIN_PHONES = ["9620211079", "9611638031"];

const App: React.FC = () => {
  const [members, setMembers] = useState<Record<string, FamilyMember>>({});
  const [posts, setPosts] = useState<Post[]>([]); 
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('tree');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isVideoTutorialsOpen, setIsVideoTutorialsOpen] = useState(false);
  const [showNotificationPermissionRequest, setShowNotificationPermissionRequest] = useState(false);
  
  // --- NEW: Invite State ---
  const [isInviteOpen, setIsInviteOpen] = useState(false);

  const [currentUser, setCurrentUser] = useState<string | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [treeFocusId, setTreeFocusId] = useState<string | null>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [pendingRelation, setPendingRelation] = useState<{ type: RelationType, relativeToId: string } | null>(null);
  
  // --- NEW: Invite popup state for newly added members ---
  const [newlyAddedMember, setNewlyAddedMember] = useState<FamilyMember | null>(null);

  // --- 0. PERSISTENT AUTHENTICATION ---
  useEffect(() => {
    const unsubAuth = authService.initializeAuth((user) => {
      if (user) {
        setFirebaseUser(user);
      } else {
        setFirebaseUser(null);
        setCurrentUser(null);
      }
      setAuthLoading(false);
    });
    return () => unsubAuth();
  }, []);

  // --- 0A. SESSION & ANALYTICS ---
  useEffect(() => {
    if (!currentUser || !firebaseUser) return;

    const familyId = members[currentUser]?.familyId || 'unknown';
    trackSessionStart(currentUser, familyId);
    trackLogin(currentUser, familyId);

    initFCM(currentUser).then((token) => {
      if (token) {
        setupFCMListeners(currentUser);
        registerNotificationClickHandler(currentUser);
      } else {
        const shown = localStorage.getItem('push_permission_shown');
        if (!shown) {
          setShowNotificationPermissionRequest(true);
          localStorage.setItem('push_permission_shown', 'true');
        }
      }
    });

    const sessionStart = Date.now();
    return () => {
      const sessionDuration = (Date.now() - sessionStart) / 1000; 
      trackSessionEnd(currentUser, familyId, sessionDuration);
    };
  }, [currentUser, firebaseUser, members]);

  // --- 0B. KEYBOARD SHORTCUTS ---
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsSearchOpen(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // --- 1. LOAD ALL MEMBERS ---
  useEffect(() => {
    const q = query(collection(db, "members"));
    
    const unsubMembers = onSnapshot(q, (snapshot) => {
      const loadedMembers: Record<string, FamilyMember> = {};
      snapshot.forEach(doc => {
        loadedMembers[doc.id] = doc.data() as FamilyMember;
      });
      
      setMembers(loadedMembers);
      
      // Auto-match user logic (only when no currentUser is set yet)
      if (firebaseUser && !currentUser) {
        const matchedMember = Object.values(loadedMembers).find(
          (m: any) => m.email === firebaseUser.email || m.email === firebaseUser.email?.toLowerCase()
        );
        if (matchedMember) {
          setCurrentUser((matchedMember as any).id);
        }
      }
      setLoading(false);
    }, (err) => {
      console.error("DB Error:", err);
      setLoading(false);
    });

    return () => { unsubMembers(); };
  }, [firebaseUser]);

  // --- 2. LOAD POSTS (All posts, filtered by getFamilyPosts) ---
  useEffect(() => {
    // Load all posts ordered by timestamp descending
    // Filtering by family is done in getFamilyPosts() to handle edge cases
    const q = query(collection(db, "posts"), orderBy("timestamp", "desc"));
    
    const unsubPosts = onSnapshot(q, (snapshot) => {
      const loadedPosts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Post));
      setPosts(loadedPosts);
      
      // EXPOSE CLEANUP FUNCTIONS TO WINDOW FOR CONSOLE ACCESS
      if (typeof window !== 'undefined') {
        (window as any).findMissingParentLinks = findMissingParentLinks;
        (window as any).generateCleanupReport = generateCleanupReport;
        (window as any).fixMissingParentLink = fixMissingParentLink;
        (window as any).cleanOrphanedChildIds = cleanOrphanedChildIds;
        console.log('[CLEANUP] Exposed functions: findMissingParentLinks, generateCleanupReport, fixMissingParentLink, cleanOrphanedChildIds');
      }
    }, (err) => {
      console.error("DB Error loading posts:", err);
    });

    return () => { unsubPosts(); };
  }, []);

  // --- FAMILY FILTERING HELPERS ---
  // Get only members from current user's family
  const getFamilyMembers = useCallback(() => {
    if (!currentUser || !members[currentUser]) return {};
    const currentUserFamilyId = members[currentUser]?.familyId;
    if (!currentUserFamilyId) return members; // No family filter yet (shouldn't happen)
    
    return Object.fromEntries(
      Object.entries(members).filter(([_, member]: [string, any]) => (member as FamilyMember).familyId === currentUserFamilyId)
    );
  }, [currentUser, members]);

  // Get only posts from current user's family
  const getFamilyPosts = useCallback(() => {
    if (!currentUser || !members[currentUser]) return [];
    const currentUserFamilyId = members[currentUser]?.familyId;
    if (!currentUserFamilyId) return posts;
    
    return posts.filter((post) => (post as any).familyId === currentUserFamilyId);
  }, [currentUser, members, posts]);

  // --- BRANCH FILTERING FOR FEED ---
  // Find user's grandparents (go up 2 levels)
  const getUserGrandparents = useCallback(() => {
    if (!currentUser || !members[currentUser]) return [];
    
    const user = members[currentUser];
    const grandparents: string[] = [];
    
    // Get parents
    if (user.parentIds && user.parentIds.length > 0) {
      for (const parentId of user.parentIds) {
        const parent = members[parentId];
        if (parent && parent.parentIds) {
          // Get grandparents
          grandparents.push(...parent.parentIds);
        }
      }
    }
    
    return [...new Set(grandparents)]; // Remove duplicates
  }, [currentUser, members]);

  // Get all descendants of a person (recursive)
  const getDescendants = useCallback((personId: string): string[] => {
    const person = members[personId];
    if (!person) return [];
    
    const descendants: string[] = [personId];
    
    // Get all children
    if (person.childrenIds && person.childrenIds.length > 0) {
      for (const childId of person.childrenIds) {
        descendants.push(...getDescendants(childId));
      }
    }
    
    return descendants;
  }, [members]);

  // Get all members in user's branch (grandparent + grandparent's siblings + all descendants)
  const getBranchMembers = useCallback((grandparentId: string): string[] => {
    const branchMembers = new Set<string>();
    
    // Add grandparent and all descendants
    const grandparentDescendants = getDescendants(grandparentId);
    grandparentDescendants.forEach(id => branchMembers.add(id));
    
    // Get grandparent's siblings
    const grandparent = members[grandparentId];
    if (grandparent && grandparent.parentIds) {
      for (const greatGrandparentId of grandparent.parentIds) {
        const greatGrandparent = members[greatGrandparentId];
        if (greatGrandparent && greatGrandparent.childrenIds) {
          // Get all siblings (other children of same parent)
          for (const siblingId of greatGrandparent.childrenIds) {
            if (siblingId !== grandparentId) {
              // Add sibling and all their descendants
              const siblingDescendants = getDescendants(siblingId);
              siblingDescendants.forEach(id => branchMembers.add(id));
            }
          }
        }
      }
    }
    
    return Array.from(branchMembers);
  }, [members, getDescendants]);

  // Get posts filtered by branch
  const getBranchPosts = useCallback((grandparentId: string) => {
    if (!currentUser || !members[currentUser]) return [];
    
    const branchMemberIds = getBranchMembers(grandparentId);
    const familyPosts = getFamilyPosts();
    
    return familyPosts.filter(post => branchMemberIds.includes(post.authorId));
  }, [currentUser, members, getBranchMembers, getFamilyPosts]);

  // --- 2. HANDLERS ---
  
  // Helper: Normalize phone for Admin Check
  const normalizePhone = (phone: string) => phone.replace(/\D/g, '').slice(-10);

  // --- NEW: Handle WhatsApp invite for newly added member ---
  const handleSendInviteToNewMember = (member: FamilyMember) => {
    // 1. Check for Phone Number
    if (!member.phoneNumber || member.phoneNumber.length < 5) {
        alert(`Please add a mobile number for ${member.name} to send an invite.`);
        return;
    }

    // 2. Get the family ID
    const currentUserMember = members[currentUser || ''];
    const familyId = currentUserMember?.familyId || member.familyId;
    
    // 3. Create invite URL with familyId parameter
    const inviteUrl = familyId 
        ? `https://famitry.in?familyId=${encodeURIComponent(familyId)}`
        : "https://famitry.in";
    
    // 4. Prepare Message
    const firstName = member.name.split(' ')[0];
    const message = `Hi ${firstName}! We are building our digital family legacy on Famitry and you are already in the tree! 🌳\n\nIt's amazing to see our family insights—like where everyone is living, what professions we are in, and our blood groups—all in one place.\n\nJoin in to see our roots and add other members you know: ${inviteUrl}\n\nWaiting for you! 👨‍👩‍👧‍👦`;
    
    // 5. Open WhatsApp
    window.open(`https://wa.me/${member.phoneNumber.replace(/\D/g, '')}?text=${encodeURIComponent(message)}`, '_blank');
    
    // 6. Close the popup
    setNewlyAddedMember(null);
  };

  // Register New User (Saves to 'members' collection)
  const handleRegisterUser = async (newMember: FamilyMember) => {
      try {
          await setDoc(doc(db, "members", newMember.id), newMember);
          setCurrentUser(newMember.id);
      } catch (err) {
          console.error("Registration failed:", err);
          alert("Failed to create profile. Check connection.");
      }
  };

  const handleSaveMember = async (formData: any) => {
    const batch = writeBatch(db); // Atomic updates
    let newId: string | null = null;
    let newlyCreatedMember: FamilyMember | null = null;
    
    if (isEditing && editingId) {
        const ref = doc(db, "members", editingId);
        batch.update(ref, formData);
    } else if (pendingRelation) {
        newId = uuidv4();
        const { type, relativeToId } = pendingRelation;
        const relative = members[relativeToId];
        
        if (!relative) {
            alert("Error: Member not found. Please try again.");
            return;
        }
        
        const newMember: FamilyMember = {
            id: newId, 
            ...formData,
            familyId: relative.familyId,  // CRITICAL: Inherit family from existing member
            createdBy: currentUser, // Important for permission checks
            parentIds: [], childrenIds: [], spouseIds: [], maritalStatus: 'Single', isMarried: false, joined: false
        };
        
        // NEW: Track for invite popup
        newlyCreatedMember = newMember;

        // Linking Logic
        if (type === 'child' || type === RelationType.CHILD || type === 'Son' || type === RelationType.SON || type === 'Daughter' || type === RelationType.DAUGHTER) {
            newMember.parentIds = [relativeToId];
            const relativeRef = doc(db, "members", relativeToId);
            batch.update(relativeRef, { childrenIds: arrayUnion(newId) });

            if (relative.spouseIds && relative.spouseIds.length > 0) {
                const spouseId = relative.spouseIds[0];
                newMember.parentIds.push(spouseId);
                const spouseRef = doc(db, "members", spouseId);
                batch.update(spouseRef, { childrenIds: arrayUnion(newId) });
            }
        } 
        else if (type === 'spouse' || type === RelationType.SPOUSE) {
            newMember.spouseIds = [relativeToId];
            newMember.maritalStatus = 'Married';
            newMember.isMarried = true;
            newMember.childrenIds = [...(relative.childrenIds || [])];
            
            // CHECK: Cross-family marriage warning
            if (newMember.familyId !== relative.familyId) {
              console.warn(`[CROSS-FAMILY MARRIAGE] Detected: familyId "${newMember.familyId}" is marrying into familyId "${relative.familyId}". To merge families, please contact admin@famitry.com`);
              alert(`⚠️ CROSS-FAMILY MARRIAGE\n\nThe person you're adding is from a different family (Family ${relative.familyId}).\n\nTo merge these families through marriage, please email:\nadmin@famitry.com\n\nInclude both phone numbers for verification.`);
              // Don't prevent the action, just warn
            }
            
            const relativeRef = doc(db, "members", relativeToId);
            batch.update(relativeRef, { spouseIds: arrayUnion(newId), maritalStatus: 'Married', isMarried: true });
        }
        else if (type === 'parent' || type === RelationType.PARENT || type === RelationType.FATHER || type === RelationType.MOTHER || type === 'Father' || type === 'Mother') {
            // When adding a parent, the new member should have the child in its childrenIds
            newMember.childrenIds = [relativeToId];
            
            // Update the child (relative) to have this new parent
            const relativeRef = doc(db, "members", relativeToId);
            const updateObj: any = { parentIds: [newId] };
            
            // FOOLPROOF: Validate gender before using it
            const parentGender = formData.gender?.trim()?.toLowerCase();
            console.log(`[DEBUG] Parent Adding: relativeToId=${relativeToId}, newId=${newId}, formData.gender='${formData.gender}', parentGender='${parentGender}'`);
            
            // Set fatherId or motherId based on the new member's gender
            if (parentGender === 'male' || parentGender === 'm') {
                updateObj.fatherId = newId;
                console.log(`[DEBUG] ✓ Adding Father: newId=${newId}, to relative=${relativeToId}, fatherId will be set to=${newId}`);
            } else if (parentGender === 'female' || parentGender === 'f') {
                updateObj.motherId = newId;
                console.log(`[DEBUG] ✓ Adding Mother: newId=${newId}, to relative=${relativeToId}, motherId will be set to=${newId}`);
            } else {
                console.warn(`[WARNING] Parent gender is INVALID: '${formData.gender}'. Cannot set fatherId or motherId!`);
                alert(`ERROR: Invalid gender '${formData.gender}' for parent. Please select Male or Female.`);
                return;
            }
            
            // VERIFICATION: Ensure updateObj has the required field
            const hasParentField = updateObj.fatherId !== undefined || updateObj.motherId !== undefined;
            if (!hasParentField) {
                console.error(`[ERROR] updateObj missing both fatherId and motherId:`, updateObj);
                alert("ERROR: Parent gender field not set. Please try again.");
                return;
            }
            
            console.log(`[DEBUG] updateObj before batch:`, updateObj);
            batch.update(relativeRef, updateObj);
        }


        const newMemberRef = doc(db, "members", newId);
        batch.set(newMemberRef, newMember);
    }

    try {
        await batch.commit();
        console.log(`[DEBUG] Batch committed successfully`);
        
        // POST-BATCH VERIFICATION: If we're adding a parent, verify motherId/fatherId was set
        if (newId && pendingRelation && (pendingRelation.type === 'parent' || pendingRelation.type === RelationType.PARENT || 
                                pendingRelation.type === RelationType.FATHER || pendingRelation.type === RelationType.MOTHER || 
                                pendingRelation.type === 'Father' || pendingRelation.type === 'Mother')) {
            try {
                // Small delay to ensure Firestore has written the data
                await new Promise(resolve => setTimeout(resolve, 200));
                
                const relativeDoc = await getDoc(doc(db, "members", pendingRelation.relativeToId));
                if (relativeDoc.exists()) {
                    const relativeData = relativeDoc.data();
                    const gender = formData?.gender?.toLowerCase();
                    
                    if (gender === 'male' || gender === 'm') {
                        if (relativeData.fatherId !== newId) {
                            console.error(`[ERROR] Post-batch verification failed: fatherId not set correctly. Expected ${newId}, got ${relativeData.fatherId}`);
                            // Show warning but don't block - batch already committed
                        } else {
                            console.log(`[DEBUG] ✓ Post-batch verification PASSED: fatherId=${relativeData.fatherId}`);
                        }
                    } else if (gender === 'female' || gender === 'f') {
                        if (relativeData.motherId !== newId) {
                            console.error(`[ERROR] Post-batch verification failed: motherId not set correctly. Expected ${newId}, got ${relativeData.motherId}`);
                            // Show warning but don't block - batch already committed
                        } else {
                            console.log(`[DEBUG] ✓ Post-batch verification PASSED: motherId=${relativeData.motherId}`);
                        }
                    }
                }
            } catch (verifyError) {
                console.error('[DEBUG] Post-batch verification error (non-critical):', verifyError);
                // Don't throw - verification is informational only
            }
        }
        
        setIsModalOpen(false);
        setPendingRelation(null);
        
        // NEW: Show invite popup if a new member was added
        if (newlyCreatedMember) {
            setNewlyAddedMember(newlyCreatedMember);
        }
    } catch (error) {
        console.error("Error committing batch:", error);
        alert("Failed to save changes. Please try again.");
    }
  };

  const handleDeleteMember = async (id: string) => {
      if (id === currentUser) return alert("Cannot delete your own profile.");
      if (!window.confirm("Delete this member? This cannot be undone.")) return;
      try {
          await deleteDoc(doc(db, "members", id));
      } catch (e) { alert("Delete failed"); }
  };

  const handleLogout = async () => {
      try {
        await authService.signOut();
        setCurrentUser(null);
        setFirebaseUser(null);
      } catch (e) { console.error(e); }
  };

  const handleAddRequest = useCallback((type: RelationType, relativeToId: string) => {
    // Validation: Prevent creating multiple parents (only one parent allowed)
    if ((type === 'parent' || type === RelationType.PARENT || type === RelationType.FATHER || type === RelationType.MOTHER || type === 'Father' || type === 'Mother')) {
      const relative = members[relativeToId];
      if (relative && relative.parentIds && relative.parentIds.length > 0) {
        alert("Only one parent can be added per person. To add another parent, use Add Spouse option from your parent card.");
        return;
      }
    }
    
    // Check if adding a spouse from a different family
    if (type === 'spouse' || type === RelationType.SPOUSE) {
      const relative = members[relativeToId];
      const currentUserFamilyId = currentUser && members[currentUser] ? members[currentUser].familyId : null;
      
      // This will typically not happen in normal flow, but warning for clarity
      if (relative && currentUserFamilyId) {
        console.log(`[SPOUSE] Adding spouse. Current family: ${currentUserFamilyId}`);
        // The spouse will be matched in MemberFormModal with an existing person
      }
    }
    
    setPendingRelation({ type, relativeToId });
    setIsEditing(false);
    setEditingId(null);
    setIsModalOpen(true);
  }, [members, currentUser]);

  const handleEditRequest = useCallback((id: string) => {
    setEditingId(id);
    setIsEditing(true);
    setIsModalOpen(true);
  }, []);

  // --- HANDLER: REAL-TIME LIKES ---
  const handleLikePost = async (postId: string) => {
    if (!currentUser) return;

    const post = posts.find(p => p.id === postId);
    if (!post) return;

    // Check if currently liked
    const isLiked = post.likes.includes(currentUser);
    
    const postRef = doc(db, "posts", postId);

    try {
        // Optimistic UI Update (Optional: Firestore is fast enough usually, but this feels instant)
        // Note: The onSnapshot listener will overwrite this momentarily with server data, which is fine.
        const updatedLikes = isLiked 
            ? post.likes.filter(id => id !== currentUser) 
            : [...post.likes, currentUser];
            
        const updatedPosts = posts.map(p => p.id === postId ? { ...p, likes: updatedLikes } : p);
        setPosts(updatedPosts);

        // Update Database Atomically
        await updateDoc(postRef, {
            likes: isLiked ? arrayRemove(currentUser) : arrayUnion(currentUser)
        });
        
        // Notify if it's a new like (and not self-like)
        if (!isLiked && post.authorId !== currentUser) {
             // Logic to create notification document here (optional)
        }

    } catch (error) {
        console.error("Error updating like:", error);
        // Revert on error (optional implementation)
    }
  };

  // --- RENDER ---
  if (loading || authLoading) return <AppSkeleton />;

  if (!currentUser || !firebaseUser) {
      return (
          <LoginScreen 
            members={members} 
            onLogin={setCurrentUser} 
            onRegister={handleRegisterUser}
            onClaim={(id) => setCurrentUser(id)}
          />
      );
  }

  const userObj = members[currentUser];
  // Calculate Admin Status
  const currentPhone = normalizePhone(userObj?.phoneNumber || '');
  const isSuperAdmin = SUPER_ADMIN_PHONES.includes(currentPhone) || SUPER_ADMIN_PHONES.some(admin => normalizePhone(admin) === currentPhone);

  return (
    <>
      <Layout 
        activeTab={activeTab} 
        onTabChange={setActiveTab} 
        currentUserId={currentUser} 
        isAdmin={isSuperAdmin} // Correctly passed bool
        onSearchOpen={() => setIsSearchOpen(true)}
        onHelpOpen={() => setIsVideoTutorialsOpen(true)}
      >
      
      {activeTab === 'feed' && (
        <SocialFeed 
            posts={getFamilyPosts()} members={getFamilyMembers()} currentUser={userObj} 
            onLocateInTree={(id) => { 
              if (id === 'invite') {
                setIsInviteOpen(true);
              } else {
                setTreeFocusId(id); 
                setActiveTab('tree');
              }
            }}
            onUpdatePosts={setPosts} 
            onLikePost={(pid) => {handleLikePost(pid)}} 
            onDeletePost={(pid) => {}}
        />
      )}
      
      {activeTab === 'tree' && (
        <FamilyTree 
            members={getFamilyMembers()} currentUser={currentUser} 
            onAddMember={handleAddRequest} 
            onEditMember={handleEditRequest} 
            onDeleteMember={handleDeleteMember}
            initialFocusId={treeFocusId} 
        />
      )}
      
      {/* Other Tabs */}
      {activeTab === 'timeline' && <FamilyTimeline members={getFamilyMembers()} posts={getFamilyPosts()} />}
      {activeTab === 'circles' && <NetworkView members={getFamilyMembers()} currentUser={userObj} onAddConnection={() => setIsInviteOpen(true)} onViewProfile={(id) => { setTreeFocusId(id); setActiveTab('tree'); }} />}
      {activeTab === 'map' && <FamilyMap members={getFamilyMembers()} onViewMember={(id) => { setTreeFocusId(id); setActiveTab('tree'); }} />}
      {activeTab === 'directory' && <Directory members={getFamilyMembers()} currentUserId={currentUser} onEdit={handleEditRequest} onLocateInTree={(id) => { setTreeFocusId(id); setActiveTab('tree'); }} />}
      {activeTab === 'insights' && <FamilyStats members={getFamilyMembers()} />}
      {activeTab === 'profile' && <Profile onNavigate={setActiveTab} onSignOut={handleLogout} members={getFamilyMembers()} posts={getFamilyPosts()} currentUser={userObj} />}
      
      {/* Admin Tab - Secured */}
      {activeTab === 'admin' && isSuperAdmin && (
        <AdminDashboard members={members} posts={getFamilyPosts()} onDeletePost={() => {}} onDeleteMember={handleDeleteMember} currentUser={userObj} />
      )}

      {/* MODALS */}
      <MemberFormModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveMember}
        isEditing={isEditing}
        initialData={isEditing && editingId ? members[editingId] : {}}
        relationType={pendingRelation?.type}
        relativeName={pendingRelation ? members[pendingRelation.relativeToId]?.name : ''}
        members={members}
      />

      <InviteModal 
        isOpen={isInviteOpen} 
        onClose={() => setIsInviteOpen(false)} 
        currentUser={userObj} 
      />

      {/* NEW: Invite popup for newly added members */}
      {newlyAddedMember && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-white w-full max-w-md rounded-3xl shadow-2xl overflow-hidden animate-scale-in">
            
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 p-6 text-center relative">
              <button 
                onClick={() => setNewlyAddedMember(null)} 
                className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
              
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 backdrop-blur-md border border-white/30">
                <MessageCircle className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-black text-white">Invite {newlyAddedMember.name}</h2>
              <p className="text-indigo-100 text-sm mt-1">Send them a WhatsApp invite to join</p>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              <div className="bg-indigo-50 border border-indigo-200 rounded-2xl p-4">
                <p className="text-sm text-gray-700 leading-relaxed">
                  Send <span className="font-bold text-indigo-700">{newlyAddedMember.name}</span> a WhatsApp message to invite them to join your family tree on Famitry.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-2">
                <button 
                  onClick={() => setNewlyAddedMember(null)}
                  className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold rounded-xl transition-all"
                >
                  Not Now
                </button>
                <button 
                  onClick={() => handleSendInviteToNewMember(newlyAddedMember)}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold rounded-xl transition-all shadow-lg flex items-center justify-center gap-2"
                >
                  <MessageCircle className="w-5 h-5" />
                  Send Invite
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </Layout>

    <SearchModal
      isOpen={isSearchOpen}
      onClose={() => setIsSearchOpen(false)}
      members={getFamilyMembers()}
      posts={getFamilyPosts()}
      onSelectMember={(memberId) => {
        setTreeFocusId(memberId);
        setActiveTab('tree');
        setIsSearchOpen(false);
      }}
      onSelectPost={() => {
        setActiveTab('feed');
        setIsSearchOpen(false);
      }}
    />

    {/* <VideoTutorials isOpen={isVideoTutorialsOpen} onClose={() => setIsVideoTutorialsOpen(false)} currentUser={currentUser || undefined} /> */}
    
    {showNotificationPermissionRequest && currentUser && (
      <NotificationPermissionRequest
        userId={currentUser}
        onClose={() => setShowNotificationPermissionRequest(false)}
      />
    )}
    
    <PWAInstallPrompt currentUser={currentUser || undefined} />
    </>
  );
};

const AppWithTheme: React.FC = () => (
  <ThemeProvider>
    <App />
  </ThemeProvider>
);

export default AppWithTheme;